import { Pnl } from '@ui/components/Amount/Pnl';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { omit } from 'lodash-es';
import { useMemo } from 'react';

import { valueStyle } from './styles';

type FundingFeeDisplayProps = {
  position: Position;
  collateralMarketPrice?: Nullable<BigNumber>;
};

export const FundingFeeDisplay = (props: FundingFeeDisplayProps) => {
  const { position, collateralMarketPrice, ...rest } = props;

  if (Object.prototype.hasOwnProperty.call(props, 'collateralMarketPrice')) {
    return (
      <FundingFeeDisplayValue
        position={position}
        collateralMarketPrice={collateralMarketPrice}
        {...rest}
      />
    );
  }

  return <FundingFeeDisplayWithHooks position={position} {...rest} />;
};

const FundingFeeDisplayWithHooks = ({
  position,
  ...props
}: {
  position: Position;
}) => {
  const collateralMarketPrice =
    usePayTokenPriceLatest(position.collateralToken) || ZERO_BIG_NUMBER;

  return (
    <FundingFeeDisplayValue
      position={position}
      collateralMarketPrice={collateralMarketPrice}
      {...props}
    />
  );
};

const FundingFeeDisplayValue = ({
  position,
  collateralMarketPrice = ZERO_BIG_NUMBER,
  ...props
}: {
  position: Position;
  collateralMarketPrice?: Nullable<BigNumber>;
}) => {
  const displayFundingFee = useMemo(() => {
    const wholeFundingFee = position
      .getFundingFeeUsd(collateralMarketPrice ?? ZERO_BIG_NUMBER)
      .plus(
        position.getHoldingFeeUsd(collateralMarketPrice ?? ZERO_BIG_NUMBER),
      );

    return wholeFundingFee.times(-1);
  }, [collateralMarketPrice, position]);

  return (
    <Pnl
      value={displayFundingFee}
      css={omit(valueStyle, 'color')}
      opts={{
        isShowSign: true,
      }}
      data-cy-value={displayFundingFee}
      {...props}
    />
  );
};
